import { pack } from "../shared/color";

/** Pixel sprite. Colour is ABGR in a uint32, alpha in the top byte, matching the original. */
export interface Pix {
  w: number;
  h: number;
  data: Uint32Array;
}

export interface RotFrame {
  sprite: Pix;
  ox: number;
  oy: number;
  canvas: HTMLCanvasElement;
}

/** Scale2x / EPX. Applied three times this is the 8× RotSprite upsample. */
export function scale2x(src: Pix): Pix {
  const w = src.w;
  const h = src.h;
  const out = new Uint32Array(w * h * 4);
  const sample = (x: number, y: number) => src.data[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const c = sample(x, y);
      const up = sample(x, y - 1);
      const right = sample(x + 1, y);
      const left = sample(x - 1, y);
      const down = sample(x, y + 1);
      let tl = c;
      let tr = c;
      let bl = c;
      let br = c;
      if (left === up && left !== down && up !== right) tl = up;
      if (up === right && up !== left && right !== down) tr = right;
      if (down === left && down !== right && left !== up) bl = left;
      if (right === down && right !== up && down !== left) br = right;
      const row = y * 2 * (w * 2) + x * 2;
      out[row] = tl;
      out[row + 1] = tr;
      out[row + w * 2] = bl;
      out[row + w * 2 + 1] = br;
    }
  }
  return { w: w * 2, h: h * 2, data: out };
}

export function flipY(src: Pix): Pix {
  const data = new Uint32Array(src.w * src.h);
  for (let y = 0; y < src.h; y++) {
    for (let x = 0; x < src.w; x++) data[y * src.w + x] = src.data[(src.h - 1 - y) * src.w + x];
  }
  return { w: src.w, h: src.h, data };
}

function canvasOf(pix: Pix): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, pix.w);
  canvas.height = Math.max(1, pix.h);
  const ctx = canvas.getContext("2d")!;
  const image = ctx.createImageData(canvas.width, canvas.height);
  const dst = image.data;
  for (let i = 0; i < pix.w * pix.h; i++) {
    const c = pix.data[i] >>> 0;
    const a = c >>> 24;
    if (!a) continue;
    const o = i * 4;
    dst[o] = c & 255;
    dst[o + 1] = (c >>> 8) & 255;
    dst[o + 2] = (c >>> 16) & 255;
    dst[o + 3] = a;
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}

/**
 * RotSprite-style cache. The source is flipped for the other facing, scaled 8×,
 * then each of `steps` angles is nearest-sampled back to sprite pixels.
 */
export class RotSprite {
  readonly steps: number;
  private readonly frames: [Array<RotFrame | undefined>, Array<RotFrame | undefined>] = [[], []];
  private readonly src: [Pix, Pix];
  private readonly big: [Pix, Pix];
  private readonly pivot: [[number, number], [number, number]];

  constructor(src: Pix, ax: number, ay: number, steps = 128) {
    this.steps = steps;
    const flipped = flipY(src);
    this.src = [src, flipped];
    this.pivot = [
      [ax, ay],
      [ax, src.h - 1 - ay],
    ];
    this.big = [scale2x(scale2x(scale2x(src))), scale2x(scale2x(scale2x(flipped)))];
  }

  get(angle: number, flip: boolean): RotFrame {
    const side = flip ? 1 : 0;
    const turn = Math.PI * 2;
    const step = Math.round((((angle % turn) + turn) % turn) / turn * this.steps) % this.steps;
    let frame = this.frames[side][step];
    if (!frame) frame = this.frames[side][step] = this.build(side, (step / this.steps) * turn);
    return frame;
  }

  quantize(angle: number): number {
    const turn = Math.PI * 2;
    return Math.round((((angle % turn) + turn) % turn) / turn * this.steps) % this.steps / this.steps * turn;
  }

  anchor(x: number, y: number, angle: number, flip: boolean): [number, number] {
    const [px, py] = this.pivot[flip ? 1 : 0];
    const sy = flip ? this.src[0].h - 1 - y : y;
    const dx = x - px;
    const dy = sy - py;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    return [dx * c - dy * s, dx * s + dy * c];
  }

  private build(side: number, angle: number): RotFrame {
    const src = this.src[side];
    const big = this.big[side];
    const [px, py] = this.pivot[side];
    const ox = px + 0.5;
    const oy = py + 0.5;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const corners: Array<[number, number]> = [
      [0, 0],
      [src.w, 0],
      [0, src.h],
      [src.w, src.h],
    ];
    let minX = 1e9;
    let minY = 1e9;
    let maxX = -1e9;
    let maxY = -1e9;
    for (const [x, y] of corners) {
      const dx = x - ox;
      const dy = y - oy;
      const rx = dx * c - dy * s;
      const ry = dx * s + dy * c;
      minX = Math.min(minX, rx);
      minY = Math.min(minY, ry);
      maxX = Math.max(maxX, rx);
      maxY = Math.max(maxY, ry);
    }
    const originX = Math.ceil(-minX - 0.5) + 1;
    const originY = Math.ceil(-minY - 0.5) + 1;
    const w = originX + Math.ceil(maxX + 0.5) + 2;
    const h = originY + Math.ceil(maxY + 0.5) + 2;
    const data = new Uint32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const dx = x - originX;
        const dy = y - originY;
        const sx = dx * c + dy * s + ox;
        const sy = -dx * s + dy * c + oy;
        if (sx < 0 || sy < 0 || sx >= src.w || sy >= src.h) continue;
        const bx = Math.floor(sx * 8);
        const by = Math.floor(sy * 8);
        const color = big.data[by * big.w + bx];
        if (color >>> 24) data[y * w + x] = color;
      }
    }
    const sprite = { w, h, data };
    return { sprite, ox: originX, oy: originY, canvas: canvasOf(sprite) };
  }
}

export function imagePix(img: CanvasImageSource, sw: number, sh: number, sx = 0, sy = 0): Pix {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, sw);
  canvas.height = Math.max(1, sh);
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  const raw = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const data = new Uint32Array(canvas.width * canvas.height);
  for (let i = 0; i < data.length; i++) {
    const a = raw[i * 4 + 3];
    if (a < 128) continue;
    data[i] = pack(raw[i * 4], raw[i * 4 + 1], raw[i * 4 + 2], a);
  }
  return { w: canvas.width, h: canvas.height, data };
}

export function drawRot(ctx: CanvasRenderingContext2D, frame: RotFrame, x: number, y: number, scale: number) {
  ctx.drawImage(frame.canvas, x - frame.ox * scale, y - frame.oy * scale, frame.sprite.w * scale, frame.sprite.h * scale);
}
